import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Users, DollarSign, Activity } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth.js';
import BreathtakingCard from './shared/BreathtakingCard.js';
import { cardVariants } from './shared/CardAnimations.js';

interface InstitutionalMetrics {
  overview: {
    aumTracked: number;
    strategyCount: number;
    agentConcurrency: number;
    systemHealth: 'green' | 'yellow' | 'red';
  };
  performance: {
    netReturn: number;
    sharpeRatio: number;
    maxDrawdown: number;
    winRate: number;
    tradeCount: number;
  };
  risk: {
    portfolioLeverage: number;
    marginBuffer: number;
    pdtRiskScore: number;
    capitalEfficiency: number;
  };
  fundMetrics: {
    nav: number;
    totalAssets: number;
    investorCount: number;
    performance: {
      daily: number;
      monthly: number;
      ytd: number;
    };
  };
  accountSummary: {
    entity: string;
    accountValue: number;
    openPnl: number;
    realizedPnl: number;
    buyingPower: number;
    pendingDeposits: number;
    cashAvailable: number;
  }[];
}

interface MetricCardProps {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ComponentType<any>;
  status: string;
  trend: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon: Icon, status, trend }) => (
  <BreathtakingCard
    title={title}
    value={value.toString()}
    subtitle={subtitle}
    variant="performance"
    size="md"
    trend={status === 'active' ? 'up' : status === 'paused' ? 'neutral' : 'down'}
    icon={<Icon className="w-6 h-6 text-blue-400" />}
  >
    <div className="mt-auto">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">{trend}</span>
        <div className={`w-2 h-2 rounded-full ${
          status === 'active' ? 'bg-green-400' : 
          status === 'paused' ? 'bg-yellow-400' : 'bg-red-400'
        }`} />
      </div>
    </div>
  </BreathtakingCard>
);

interface RiskIndicatorProps {
  label: string;
  value: number;
  threshold: number;
}

const RiskIndicator: React.FC<RiskIndicatorProps> = ({ label, value, threshold }) => (
  <div className="space-y-2">
    <div className="flex justify-between text-sm">
      <span className="text-slate-300">{label}</span>
      <span className={value > threshold ? 'text-red-400' : 'text-green-400'}>
        {(value * 100).toFixed(1)}%
      </span>
    </div>
    <div className="w-full bg-slate-700 rounded-full h-2">
      <div 
        className={`h-2 rounded-full transition-all duration-300 ${
          value > threshold ? 'bg-red-500' : 'bg-green-500'
        }`}
        style={{ width: `${Math.min(value * 100, 100)}%` }}
      />
    </div>
  </div>
);

const OversightDashboard: React.FC = () => {
  const { hasRole } = useAuth();
  const [metrics, setMetrics] = useState<InstitutionalMetrics | null>(null);
  const [loading, setLoading] = useState(true);

  const isAdmin = hasRole('admin') || hasRole('fund_manager');

  useEffect(() => {
    fetchTradingMetrics();
    const interval = setInterval(fetchTradingMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTradingMetrics = async () => {
    try {
      const [overviewResponse, performanceResponse, riskResponse, accountResponse] = await Promise.all([
        fetch('/api/arcoversight/metrics/overview'),
        fetch('/api/arcoversight/metrics/performance/overview'),
        fetch('/api/arcoversight/metrics/risk/current'),
        fetch('/api/arcoversight/accounts/summary')
      ]);

      const overviewData = overviewResponse.ok ? await overviewResponse.json() : null;
      const performanceData = performanceResponse.ok ? await performanceResponse.json() : null;
      const riskData = riskResponse.ok ? await riskResponse.json() : null;
      const accountData = accountResponse.ok ? await accountResponse.json() : null;

      setMetrics({
        overview: {
          aumTracked: overviewData?.data?.aumTracked || 5000000,
          strategyCount: overviewData?.data?.strategyCount || 4,
          agentConcurrency: overviewData?.data?.agentConcurrency || 8,
          systemHealth: overviewData?.data?.systemHealth || 'green'
        },
        performance: {
          netReturn: performanceData?.data?.netReturn || 12.4,
          sharpeRatio: performanceData?.data?.sharpeRatio || 1.85,
          maxDrawdown: performanceData?.data?.maxDrawdown || -8.7,
          winRate: performanceData?.data?.winRate || 68,
          tradeCount: performanceData?.data?.tradeCount || 1247
        },
        risk: {
          portfolioLeverage: riskData?.data?.portfolioLeverage || 1.2,
          marginBuffer: riskData?.data?.marginBuffer || 0.35,
          pdtRiskScore: riskData?.data?.pdtRiskScore || 0.15,
          capitalEfficiency: riskData?.data?.capitalEfficiency || 0.78
        },
        fundMetrics: {
          nav: 1250000,
          totalAssets: overviewData?.data?.aumTracked || 5000000,
          investorCount: 25,
          performance: {
            daily: performanceData?.data?.dailyReturn || 0.12,
            monthly: 2.34,
            ytd: performanceData?.data?.netReturn || 15.67
          }
        },
        accountSummary: accountData?.data || []
      });
    } catch (error) {
      console.error('Failed to fetch institutional metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400" />
          <span className="text-brand-text text-lg [text-shadow:_0_1px_2px_rgb(0_0_0_/_60%)]">Loading oversight data...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 min-h-screen">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between"
      >
        <h1 className="text-3xl font-bold text-yellow-400">arcOversight Dashboard</h1>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-sm text-slate-300">Live Data</span>
        </div>
      </motion.div>

      <motion.div 
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
        variants={cardVariants}
        initial="hidden"
        animate="visible"
      >
        {isAdmin && (
          <>
            <motion.div custom={0} variants={cardVariants}>
              <MetricCard
                title="AUM Tracked"
                value={`$${((metrics?.overview.aumTracked || 0) / 1000000).toFixed(1)}M`}
                subtitle="Assets Under Management"
                icon={DollarSign}
                status={metrics?.overview.systemHealth === 'green' ? 'active' : 'error'}
                trend={`${metrics?.performance.netReturn || 0}% YTD`}
              />
            </motion.div>
            <motion.div custom={1} variants={cardVariants}>
              <MetricCard
                title="Strategy Count"
                value={metrics?.overview.strategyCount || 0}
                subtitle="Active Strategies"
                icon={Activity}
                status="active"
                trend={`${metrics?.overview.agentConcurrency || 0} agents`}
              />
            </motion.div>
          </>
        )}
        <motion.div custom={isAdmin ? 2 : 0} variants={cardVariants}>
          <MetricCard
            title="Fund NAV"
            value={`$${(metrics?.fundMetrics.nav || 0).toLocaleString()}`}
            subtitle="Net Asset Value"
            icon={DollarSign}
            status="active"
            trend={`${metrics?.fundMetrics.performance.daily || 0}% today`}
          />
        </motion.div>
        <motion.div custom={isAdmin ? 3 : 1} variants={cardVariants}>
          <MetricCard
            title="Investors"
            value={metrics?.fundMetrics.investorCount || 0}
            subtitle="Active LPs"
            icon={Users}
            status="active"
            trend="Growing"
          />
        </motion.div>

        {/* Account Information Cards */}
        {metrics?.accountSummary && metrics.accountSummary.length > 0 && (
          <>
            <motion.div custom={isAdmin ? 4 : 2} variants={cardVariants}>
              <MetricCard
                title="Account Value"
                value={`$${metrics.accountSummary[0].accountValue.toLocaleString()}`}
                subtitle="Total Account Value"
                icon={DollarSign}
                status="active"
                trend={`${metrics.accountSummary[0].entity}`}
              />
            </motion.div>
            <motion.div custom={isAdmin ? 5 : 3} variants={cardVariants}>
              <MetricCard
                title="Buying Power"
                value={`$${metrics.accountSummary[0].buyingPower.toLocaleString()}`}
                subtitle="Available Buying Power"
                icon={Activity}
                status="active"
                trend={`$${metrics.accountSummary[0].cashAvailable.toLocaleString()} cash`}
              />
            </motion.div>
            <motion.div custom={isAdmin ? 6 : 4} variants={cardVariants}>
              <MetricCard
                title="Open P/L"
                value={`$${metrics.accountSummary[0].openPnl.toLocaleString()}`}
                subtitle="Unrealized P&L"
                icon={Activity}
                status={metrics.accountSummary[0].openPnl >= 0 ? "active" : "error"}
                trend={`$${metrics.accountSummary[0].realizedPnl.toLocaleString()} realized`}
              />
            </motion.div>
          </>
        )}
      </motion.div>

      {isAdmin && (
        <motion.div 
          className="grid grid-cols-1 xl:grid-cols-2 gap-4 md:gap-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.6 }}
        >
          <BreathtakingCard
            title="Trading Performance"
            variant="performance"
            size="lg"
            className="col-span-1"
          >
            <div className="h-64 flex items-center justify-center text-slate-400">
              Performance Chart (Integration with existing chart components)
            </div>
          </BreathtakingCard>
          
          <BreathtakingCard
            title="Risk Monitoring"
            variant="risk"
            size="lg"
            className="col-span-1"
          >
            <div className="space-y-4">
              <RiskIndicator
                label="Portfolio Leverage"
                value={metrics?.risk.portfolioLeverage ? (metrics.risk.portfolioLeverage - 1) : 0.2}
                threshold={0.5}
              />
              <RiskIndicator
                label="PDT Risk Score"
                value={metrics?.risk.pdtRiskScore || 0.15}
                threshold={0.3}
              />
              <RiskIndicator
                label="Capital Efficiency"
                value={metrics?.risk.capitalEfficiency || 0.78}
                threshold={0.9}
              />
            </div>
          </BreathtakingCard>
        </motion.div>
      )}
    </div>
  );
};

export default OversightDashboard;
